import React, { useState, useRef, useEffect, useCallback } from "react";
import Button from "../Button/Button";
import { compressImageToSize } from "../../utils/imageCompression.util";

/**
 * Reusable Image Cropper Component
 * @param {Object} props
 * @param {File} props.imageFile - Original image file
 * @param {Function} props.onCropComplete - Callback with cropped File
 * @param {Function} props.onCancel - Cancel callback
 * @param {number} props.aspectRatio - Target aspect ratio (default: 16/9)
 * @param {number} props.maxSizeMB - Maximum file size in MB (default: 1)
 * @param {boolean} props.showCompression - Show compression option (default: true)
 */
const ImageCropper = ({
  imageFile,
  onCropComplete,
  onCancel,
  aspectRatio = 16 / 9,
  maxSizeMB = 1,
  showCompression = true,
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null); // 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Initialize crop area function - defined before useEffects that use it
  const initializeCropArea = useCallback((imgWidth, imgHeight) => {
    if (!containerRef.current) {
      console.warn("Container ref not available for crop area initialization");
      return;
    }
    
    const containerWidth = containerRef.current.clientWidth || 600;
    const containerHeight = containerRef.current.clientHeight || 400;

    // Calculate display dimensions
    const imgAspect = imgWidth / imgHeight;
    const containerAspect = containerWidth / containerHeight;

    let displayWidth, displayHeight;
    if (imgAspect > containerAspect) {
      displayWidth = containerWidth;
      displayHeight = containerWidth / imgAspect;
    } else {
      displayHeight = containerHeight;
      displayWidth = containerHeight * imgAspect;
    }

    // Calculate crop area maintaining aspect ratio
    let cropWidth, cropHeight;
    if (displayWidth / displayHeight > aspectRatio) {
      cropHeight = displayHeight;
      cropWidth = cropHeight * aspectRatio;
    } else {
      cropWidth = displayWidth;
      cropHeight = cropWidth / aspectRatio;
    }

    const x = (displayWidth - cropWidth) / 2;
    const y = (displayHeight - cropHeight) / 2;

    setCropArea({
      x,
      y,
      width: cropWidth,
      height: cropHeight,
      displayWidth,
      displayHeight,
      imgWidth,
      imgHeight,
    });
  }, [aspectRatio]);

  // Load image when file changes
  useEffect(() => {
    if (!imageFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  // Initialize crop area when image loads
  useEffect(() => {
    if (!imageSrc || !imageRef.current) return;

    const img = imageRef.current;
    
    const initCrop = () => {
      // Ensure container has dimensions
      if (!containerRef.current) {
        requestAnimationFrame(initCrop);
        return;
      }
      
      const container = containerRef.current;
      if (container.clientWidth === 0 || container.clientHeight === 0) {
        // Wait for next frame if container isn't ready
        requestAnimationFrame(initCrop);
        return;
      }
      
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setImageLoaded(true);
        initializeCropArea(img.naturalWidth, img.naturalHeight);
      }
    };
    
    // Check if image is already loaded
    if (img.complete && img.naturalWidth > 0) {
      // Use requestAnimationFrame to ensure container is ready
      requestAnimationFrame(initCrop);
    } else {
      img.onload = () => {
        requestAnimationFrame(initCrop);
      };
    }
  }, [imageSrc, aspectRatio, initializeCropArea]);

  const handleMouseDown = (e) => {
    if (!imageLoaded || !cropArea.displayWidth) return;
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Convert screen coordinates to crop area coordinates
    const scaleX = cropArea.displayWidth / rect.width;
    const scaleY = cropArea.displayHeight / rect.height;
    const cropX = cropArea.x / scaleX;
    const cropY = cropArea.y / scaleY;
    const cropW = cropArea.width / scaleX;
    const cropH = cropArea.height / scaleY;

    // Check if clicking on a resize handle
    const handleSize = 25; // Size of handle area

    // Check corners first (they have priority)
    if (Math.abs(x - cropX) < handleSize && Math.abs(y - cropY) < handleSize) {
      setIsResizing(true);
      setResizeHandle('nw');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (Math.abs(x - (cropX + cropW)) < handleSize && Math.abs(y - cropY) < handleSize) {
      setIsResizing(true);
      setResizeHandle('ne');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (Math.abs(x - cropX) < handleSize && Math.abs(y - (cropY + cropH)) < handleSize) {
      setIsResizing(true);
      setResizeHandle('sw');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (Math.abs(x - (cropX + cropW)) < handleSize && Math.abs(y - (cropY + cropH)) < handleSize) {
      setIsResizing(true);
      setResizeHandle('se');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (Math.abs(y - cropY) < handleSize && x >= cropX - 5 && x <= cropX + cropW + 5) {
      setIsResizing(true);
      setResizeHandle('n');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (Math.abs(y - (cropY + cropH)) < handleSize && x >= cropX - 5 && x <= cropX + cropW + 5) {
      setIsResizing(true);
      setResizeHandle('s');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (Math.abs(x - cropX) < handleSize && y >= cropY - 5 && y <= cropY + cropH + 5) {
      setIsResizing(true);
      setResizeHandle('w');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (Math.abs(x - (cropX + cropW)) < handleSize && y >= cropY - 5 && y <= cropY + cropH + 5) {
      setIsResizing(true);
      setResizeHandle('e');
      setResizeStart({ x, y, cropX: cropArea.x, cropY: cropArea.y, width: cropArea.width, height: cropArea.height });
    } else if (
      x >= cropX &&
      x <= cropX + cropW &&
      y >= cropY &&
      y <= cropY + cropH
    ) {
      // Click is within crop area - drag to move
      setIsDragging(true);
      setDragStart({
        x: (x - cropX) * scaleX,
        y: (y - cropY) * scaleY,
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!imageLoaded || !cropArea.displayWidth) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    if (isResizing && resizeHandle) {
      // Handle resizing
      const scaleX = cropArea.displayWidth / rect.width;
      const scaleY = cropArea.displayHeight / rect.height;

      const deltaX = (mouseX - resizeStart.x) * scaleX;
      const deltaY = (mouseY - resizeStart.y) * scaleY;

      let newX = resizeStart.cropX;
      let newY = resizeStart.cropY;
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      switch (resizeHandle) {
        case 'nw': // Top-left corner
          newWidth = resizeStart.width - deltaX;
          newHeight = newWidth / aspectRatio;
          newX = resizeStart.cropX + deltaX;
          newY = resizeStart.cropY + (resizeStart.height - newHeight);
          break;
        case 'ne': // Top-right corner
          newWidth = resizeStart.width + deltaX;
          newHeight = newWidth / aspectRatio;
          newY = resizeStart.cropY + (resizeStart.height - newHeight);
          break;
        case 'sw': // Bottom-left corner
          newWidth = resizeStart.width - deltaX;
          newHeight = newWidth / aspectRatio;
          newX = resizeStart.cropX + deltaX;
          break;
        case 'se': // Bottom-right corner
          newWidth = resizeStart.width + deltaX;
          newHeight = newWidth / aspectRatio;
          break;
        case 'n': // Top edge
          newHeight = resizeStart.height - deltaY;
          newWidth = newHeight * aspectRatio;
          newX = resizeStart.cropX - (newWidth - resizeStart.width) / 2;
          newY = resizeStart.cropY + (resizeStart.height - newHeight);
          break;
        case 's': // Bottom edge
          newHeight = resizeStart.height + deltaY;
          newWidth = newHeight * aspectRatio;
          newX = resizeStart.cropX - (newWidth - resizeStart.width) / 2;
          break;
        case 'w': // Left edge
          newWidth = resizeStart.width - deltaX;
          newHeight = newWidth / aspectRatio;
          newX = resizeStart.cropX + deltaX;
          newY = resizeStart.cropY - (newHeight - resizeStart.height) / 2;
          break;
        case 'e': // Right edge
          newWidth = resizeStart.width + deltaX;
          newHeight = newWidth / aspectRatio;
          newY = resizeStart.cropY - (newHeight - resizeStart.height) / 2;
          break;
        default:
          break;
      }

      // Ensure minimum size
      const minSize = 50;
      if (newWidth < minSize) {
        newWidth = minSize;
        newHeight = newWidth / aspectRatio;
      }
      if (newHeight < minSize) {
        newHeight = minSize;
        newWidth = newHeight * aspectRatio;
      }

      // Constrain within image bounds
      newX = Math.max(0, Math.min(newX, cropArea.displayWidth - newWidth));
      newY = Math.max(0, Math.min(newY, cropArea.displayHeight - newHeight));
      
      // Adjust if out of bounds
      if (newX + newWidth > cropArea.displayWidth) {
        newWidth = cropArea.displayWidth - newX;
        newHeight = newWidth / aspectRatio;
      }
      if (newY + newHeight > cropArea.displayHeight) {
        newHeight = cropArea.displayHeight - newY;
        newWidth = newHeight * aspectRatio;
      }

      setCropArea((prev) => ({
        ...prev,
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      }));
    } else if (isDragging) {
      // Handle dragging
      const scaleX = cropArea.displayWidth / rect.width;
      const scaleY = cropArea.displayHeight / rect.height;
      const x = (mouseX * scaleX) - dragStart.x;
      const y = (mouseY * scaleY) - dragStart.y;

      // Constrain crop area within image bounds
      const maxX = cropArea.displayWidth - cropArea.width;
      const maxY = cropArea.displayHeight - cropArea.height;

      setCropArea((prev) => ({
        ...prev,
        x: Math.max(0, Math.min(x, maxX)),
        y: Math.max(0, Math.min(y, maxY)),
      }));
    }
  }, [isDragging, isResizing, resizeHandle, imageLoaded, dragStart, resizeStart, cropArea, aspectRatio]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  const handleCrop = async () => {
    if (!imageRef.current || !canvasRef.current || !cropArea.displayWidth || !cropArea.displayHeight) {
      console.error("Cannot crop: Missing required refs or crop area not initialized", {
        hasImage: !!imageRef.current,
        hasCanvas: !!canvasRef.current,
        cropArea
      });
      return;
    }

    setIsProcessing(true);

    try {
      const img = imageRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      // Calculate actual crop coordinates on original image
      const scaleX = img.naturalWidth / cropArea.displayWidth;
      const scaleY = img.naturalHeight / cropArea.displayHeight;

      const sourceX = cropArea.x * scaleX;
      const sourceY = cropArea.y * scaleY;
      const sourceWidth = cropArea.width * scaleX;
      const sourceHeight = cropArea.height * scaleY;

      // Set canvas size to crop dimensions
      canvas.width = sourceWidth;
      canvas.height = sourceHeight;

      // Draw cropped image
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        sourceWidth,
        sourceHeight
      );

      // Convert to blob
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            setIsProcessing(false);
            return;
          }

          // Create File from blob
          const croppedFile = new File(
            [blob],
            imageFile.name,
            {
              type: imageFile.type || "image/jpeg",
              lastModified: Date.now(),
            }
          );

          // Compress if needed
          let finalFile = croppedFile;
          if (showCompression && croppedFile.size > maxSizeMB * 1024 * 1024) {
            finalFile = await compressImageToSize(croppedFile, maxSizeMB, aspectRatio);
          }

          onCropComplete(finalFile);
          setIsProcessing(false);
        },
        imageFile.type || "image/jpeg",
        0.95
      );
    } catch (error) {
      console.error("Error cropping image:", error);
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  if (!imageSrc) return null;

  return (
    <div className="w-full">
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">
          Drag the crop area to move, or drag the edges/corners to resize. Maintains {aspectRatio === 16 / 9 ? "16:9" : `${aspectRatio.toFixed(2)}:1`} aspect ratio.
        </p>
      </div>

      <div
        ref={containerRef}
        className="relative w-full bg-gray-100 rounded-lg overflow-hidden"
        style={{ minHeight: "400px", maxHeight: "600px" }}
        onMouseDown={handleMouseDown}
      >
        {imageSrc && (
          <img
            ref={imageRef}
            src={imageSrc}
            alt="Crop preview"
            className="w-full h-auto"
            style={{ display: "block", opacity: imageLoaded ? 1 : 0.5 }}
            onLoad={() => {
              // Fallback: ensure image loads even if onload didn't fire in useEffect
              if (!imageLoaded && imageRef.current?.naturalWidth > 0) {
                const img = imageRef.current;
                if (containerRef.current?.clientWidth > 0) {
                  setImageLoaded(true);
                  initializeCropArea(img.naturalWidth, img.naturalHeight);
                }
              }
            }}
          />
        )}

        {/* Crop Overlay */}
        {imageLoaded && cropArea.displayWidth > 0 && (
          <>
            {/* Top overlay */}
            <div
              className="absolute bg-black bg-opacity-50"
              style={{
                top: 0,
                left: 0,
                right: 0,
                height: `${(cropArea.y / cropArea.displayHeight) * 100}%`,
              }}
            />
            {/* Bottom overlay */}
            <div
              className="absolute bg-black bg-opacity-50"
              style={{
                bottom: 0,
                left: 0,
                right: 0,
                height: `${((cropArea.displayHeight - cropArea.y - cropArea.height) / cropArea.displayHeight) * 100}%`,
              }}
            />
            {/* Left overlay */}
            <div
              className="absolute bg-black bg-opacity-50"
              style={{
                top: `${(cropArea.y / cropArea.displayHeight) * 100}%`,
                left: 0,
                width: `${(cropArea.x / cropArea.displayWidth) * 100}%`,
                height: `${(cropArea.height / cropArea.displayHeight) * 100}%`,
              }}
            />
            {/* Right overlay */}
            <div
              className="absolute bg-black bg-opacity-50"
              style={{
                top: `${(cropArea.y / cropArea.displayHeight) * 100}%`,
                right: 0,
                width: `${((cropArea.displayWidth - cropArea.x - cropArea.width) / cropArea.displayWidth) * 100}%`,
                height: `${(cropArea.height / cropArea.displayHeight) * 100}%`,
              }}
            />

            {/* Crop area border */}
            <div
              className="absolute border-2 border-white shadow-lg"
              style={{
                left: `${(cropArea.x / cropArea.displayWidth) * 100}%`,
                top: `${(cropArea.y / cropArea.displayHeight) * 100}%`,
                width: `${(cropArea.width / cropArea.displayWidth) * 100}%`,
                height: `${(cropArea.height / cropArea.displayHeight) * 100}%`,
                cursor: isDragging ? 'grabbing' : 'move',
              }}
            >
              {/* Corner handles - resize */}
              <div 
                className="absolute -top-2 -left-2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-nwse-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
              <div 
                className="absolute -top-2 -right-2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-nesw-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
              <div 
                className="absolute -bottom-2 -left-2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-nesw-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
              <div 
                className="absolute -bottom-2 -right-2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-nwse-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
              
              {/* Edge handles - resize */}
              <div 
                className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-ns-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
              <div 
                className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-ns-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
              <div 
                className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-ew-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
              <div 
                className="absolute -right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-white border-2 border-orange-500 rounded-full cursor-ew-resize hover:bg-orange-100"
                style={{ zIndex: 10 }}
              ></div>
            </div>
          </>
        )}
      </div>

      {/* Hidden canvas for cropping */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mt-4">
        <Button
          text="Cancel"
          onClick={onCancel}
          className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
          disabled={isProcessing}
        />
        <Button
          text={isProcessing ? "Processing..." : "Crop & Continue"}
          onClick={handleCrop}
          className="bg-orange-primary text-white px-4 py-2 rounded"
          disabled={isProcessing || !imageLoaded}
        />
      </div>
    </div>
  );
};

export default ImageCropper;

